import React, {useContext, useMemo} from "react";
import {FormikProps} from "formik";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import {Bet} from "../../generated/smg_service_pb";
import {BetOutcome} from "../../generated/partner_service_pb";
import Div from "../lib/Div";
import P from "../lib/P";
import TargetsBlock from "./TargetsBlock";
import {StoreContext} from "./StoreContextProvider";

type Props = { bet?: Bet, formik: FormikProps<{ contestAmount: string }>, maxShotAmount: number, isDisabled: boolean };

const FullSwingPayoutBlock = ({bet, formik, maxShotAmount, isDisabled}: Props) => {
  // Per-player bet snapshot is sourced from the existing kiosk-stream
  // subscription (PartnerKioskUpdate.UserSessionUpdateData), populated by
  // partner-service from the same participant row that drives the
  // round-robin web stream. Mobile does not need a second subscription
  // to FullSwingSessionParticipationDetailsStream — Main.tsx writes each
  // USER_SESSION_UPDATE to StoreContext.userSessionData and we read it
  // here. When the user has not joined a session yet, userSessionData is
  // undefined and the snapshot defaults to all-zero / UNSPECIFIED, which
  // collapses every TargetsBlock row except the next-shot one (which falls
  // back to bet.nextShotTargetYards via the previewedNextTargetYards prop
  // below — kept for the very-first-shot case before the participant row
  // has any preview written).
  const {userSessionData} = useContext(StoreContext);

  // SMG-3401: keep the settled-shot result (the previous-bet row) visible
  // during the settle→re-arm gap, after the bet has cleared. The outcome is
  // carried on userSessionData (set by the PLAYER_BET_SETTLED frame), so it
  // survives the `bet` prop going undefined. Mirrors TargetsBlock's own
  // `showPrevious` gate so the block renders iff that row would.
  const hasPreviousResult = (userSessionData?.previousTargetOutcome ?? BetOutcome.UNSPECIFIED) !== BetOutcome.UNSPECIFIED && Number(userSessionData?.previousTargetDistanceYards ?? 0) > 0;

  const payLines = useMemo(() => {
    if (!bet) return [];

    const baseAmount = bet.betState === "open" ? formik.values.contestAmount === "-2" ? maxShotAmount : +formik.values.contestAmount * 100 : Number(bet.amount);

    const list = bet.challenges.reduce((acc: any[], challenge, i) => {
      if (bet.paylines[i].multiplier) {
        acc.push({
          ft: Math.round(Number(challenge.value2) / 12),
          amount: Number(bet.paylines[i].multiplier) / 100 * (baseAmount || 0) / 100
        });
      }

      return acc;
    }, []) || [];

    list.forEach((el, i) => {
      if (i === 0) {
        el.w = 110;
        el.bg = "#FF00FF66";
        el.borderColor = "#F0F";

      } else if (i === 1) {
        el.w = 100;
        el.bg = "#FF15004D";
        el.borderColor = "#FF1500";

      } else if (i === 2) {
        el.w = 90;
        el.bg = "#FF7A0040";
        el.borderColor = "#FF7A00";

      } else if (i === 3) {
        el.w = 80;
        el.bg = "#FFFF0033";
        el.borderColor = "#FF0";

      } else if (i === 4) {
        el.w = 70;
        el.bg = "#2DFF1A33";
        el.borderColor = "#2DFF1A";
      }
    });

    return list;
  }, [bet, formik.values.contestAmount, maxShotAmount]);

  const formattedAmount = (value: number) => {
    const options: Intl.NumberFormatOptions = {
      style: "currency", currency: "USD", minimumFractionDigits: value % 1 === 0 ? 0 : 2, maximumFractionDigits: 2
    };

    return new Intl.NumberFormat("en-US", options).format(value);
  };

  return (
    <Div row p={16} justifyContent="space-between" opacity={isDisabled ? 0.25 : 1}>
      <Div column gap={6}>
        <P fontFamily="DavisSans-Regular" ml={8}>Payout</P>

        {!!bet?.holeInOneBonusAmount && (
          <Div row gap={10} alignItems="center">
            <Div row justifyContent="space-between" alignItems="center" w={120} pl={8} pr={3} roundedLeft={4}
                 roundedRight={24} bg="#7F66FF66" borderWidth={1} borderColor="#7F66FF">
              <P fontFamily="DavisSans-Medium" lh={28}>{formattedAmount(bet.holeInOneBonusAmount / 100)}</P>
              <MaterialCommunityIcons name="bullseye" size={24} color="white"/>
            </Div>

            <P fontFamily="DavisSans-Regular">Ace!</P>
          </Div>
        )}

        {payLines.filter(el => !!el.w).map((el, i) => (
          <Div row gap={10} alignItems="center" key={el.amount + `${i}` + el.ft}>
            <Div w={el.w} pl={8} pr={3} roundedLeft={4} roundedRight={24} bg={el.bg} borderWidth={1}
                 borderColor={el.borderColor}>
              <P fontFamily="DavisSans-Medium" lh={28}>{formattedAmount(el.amount)}</P>
            </Div>

            <P fontFamily="DavisSans-Regular">{el.ft}ft</P>
          </Div>
        ))}
      </Div>

      <Div>
        <Div flex={1}/>
        {(!!bet || hasPreviousResult) && (
          // The cross-bet [previous, current, next] target window comes
          // straight from the kiosk-stream USER_SESSION_UPDATE — populated
          // by partner-service from the streaming player's participant
          // row, which is itself maintained by the bet_filled_partner /
          // bet_accepted_partner / bet_resolved_partner consumers. Each
          // field defaults safely to 0 / UNSPECIFIED / false when no
          // userSessionData is in the store yet (pre-join, or before the
          // first kiosk update arrives), which collapses the unrelated
          // rows in TargetsBlock per its showPrevious / showCurrent /
          // showNext gates.
          <TargetsBlock
            targetDistanceYards={Number(userSessionData?.targetDistanceYards ?? 0)}
            previewedTargetDistanceYards={Number(userSessionData?.previewedTargetDistanceYards ?? 0)}
            previewedNextTargetYards={Number(userSessionData?.previewedNextTargetYards ?? 0)}
            previousTargetDistanceYards={Number(userSessionData?.previousTargetDistanceYards ?? 0)}
            previousTargetOutcome={userSessionData?.previousTargetOutcome ?? BetOutcome.UNSPECIFIED}
          />
        )}
      </Div>
    </Div>
  );
};

export default FullSwingPayoutBlock;
